import { Button, DatePicker, Flex, Table } from 'antd'
import { useState } from 'react'
import { useParams } from 'react-router-dom'

import { useTreatmentServiceGetListPatientNewbornsEndpoint } from 'src/api/queries'
import { NewbornItemDto, NewBornStatus } from 'src/api/requests'
import { Select } from 'src/components'
import { useNewBornColumns } from 'src/features/persons/components/PregnancyMonitoring/hooks'
import { useDefaultSortingItem, useTablePagination, useUser } from 'src/hooks'
import { dayMonthYearBackSlash } from 'src/libs'
import { useLocales } from 'src/locales'
import { resetPagination } from 'src/utils'

import { PersonNewBornDetailsModal } from './PersonNewBornDetailsModal'

export const PersonNewBornTable = () => {
  const { t } = useLocales()
  const { id, personId } = useParams()

  const { userData } = useUser()
  const pagination = useTablePagination()
  const [currentNewBornId, setCurrentNewBornId] = useState<string>('')

  const { columns } = useNewBornColumns()

  const [birthDate, setBirthDate] = useState<string[]>([])
  const [status, setStatus] = useState('')

  const { defaultSortingOption } = useDefaultSortingItem()

  const queryParams = {
    pageNumber: pagination.current,
    pageSize: pagination.pageSize,
    institutionIds: [String(userData?.currentLegalEntityId)],
    ...(birthDate?.[0] &&
      birthDate?.[1] && {
        birthDateFrom: birthDate?.[0],
        birthDateTo: birthDate?.[1]
      }),
    ...(status && { status: [status] as NewBornStatus[] }),
    personIds: [String(personId)],
    pregnancyMonitoringIds: [String(id)]
  }

  const { data, isLoading } =
    useTreatmentServiceGetListPatientNewbornsEndpoint(queryParams)

  const onSelect = (val: string) => {
    setStatus(val)
    resetPagination(pagination)
  }

  const onBirthDateSelect = (val: string[]) => {
    setBirthDate(val)
    resetPagination(pagination)
  }

  const newBornStatusOptions = [
    defaultSortingOption,
    ...Object.values(NewBornStatus).map((item) => ({
      value: item,
      label: t(`pregnancy.newborn_statuses.${item}`)
    }))
  ]

  const newColumns = [
    ...columns,
    {
      key: 'see_details',
      title: t('common.see_details'),
      render: (record: NewbornItemDto) => (
        <Button
          type="link"
          onClick={() => setCurrentNewBornId(String(record?.id))}
        >
          {t('common.see_details')}
        </Button>
      )
    }
  ]

  return (
    <Flex vertical gap={16}>
      <Flex gap={8} align="end" justify="end">
        <DatePicker.RangePicker
          onChange={(_, dateStrings) => onBirthDateSelect(dateStrings)}
          format={dayMonthYearBackSlash}
          placeholder={[
            t('pregnancy.birth_date_from'),
            t('pregnancy.birth_date_to')
          ]}
        />

        <Select
          onSelect={onSelect}
          placeholder={t('pregnancy.newborn_status')}
          options={newBornStatusOptions}
        />
      </Flex>

      <Table
        rowKey="id"
        scroll={{ x: 'max-content' }}
        loading={isLoading}
        columns={newColumns}
        dataSource={data?.data}
        pagination={{ ...pagination, total: data?.totalCount }}
      />

      {currentNewBornId && (
        <PersonNewBornDetailsModal
          id={currentNewBornId}
          onCancel={() => setCurrentNewBornId('')}
        />
      )}
    </Flex>
  )
}
